/* global google */
/*
 * Mapa canónico de dirección de comercio (COMPARTIDO panel + portal partner).
 *
 * Extraído del bloque de mapa de `src/pages/Setup/forms/AddressForm.tsx`: GoogleMap + Autocomplete
 * superpuesto + MarkerF con el pin azul de Plick (`@assets/images/marker.png`, 40x65). Prop-driven:
 * SIN Redux ni @plick/ui. El consumidor carga Google Places (su propio loader: useJsApiLoader o
 * GoogleMapsContext — misma key+libraries ⇒ singleton) y pasa `isLoaded`, el estado y los callbacks.
 *
 * Sincronía bidireccional: elegir un lugar (onPlaceSelected) recentra/mueve el pin vía el estado del
 * consumidor; arrastrar el pin (onMarkerDragEnd) fija las coordenadas exactas.
 *
 * Layout: el buscador va ARRIBA del mapa (columna vertical), NO superpuesto — así el mapa nunca queda
 * tapado y el usuario no pierde contexto al escribir.
 */
import { useRef, type CSSProperties } from 'react';
import { Autocomplete, GoogleMap, MarkerF } from '@react-google-maps/api';
import CustomMarker from '@assets/images/marker.png';
import { V4 } from '@components/v4/tokens';

const FONT = 'Poppins, sans-serif';

export interface LatLng {
  lat: number;
  lng: number;
}

export interface CommerceAddressMapProps {
  /** El consumidor carga Google Places (singleton) y pasa su estado de carga. */
  isLoaded: boolean;
  /** Centro del mapa. */
  center: LatLng;
  /** true cuando ya hay coordenadas guardadas (acerca el zoom). */
  hasSavedLocation: boolean;
  /** Posición del pin azul arrastrable. */
  markerPosition: LatLng;
  /** Se elige un lugar en el autocompletado → place crudo de Google (incluye address_components). */
  onPlaceSelected: (place: google.maps.places.PlaceResult) => void;
  /** Se suelta el pin → coordenadas exactas. */
  onMarkerDragEnd: (lat: number, lng: number) => void;
  /** Placeholder del buscador superpuesto. */
  searchPlaceholder?: string;
  /** Alto del mapa (el consumidor lo deriva con useDesktop para mantener responsive). */
  height?: number | string;
}

const MAP_OPTIONS: google.maps.MapOptions = {
  fullscreenControl: false,
  streetViewControl: false,
  mapTypeControl: false,
};

// Mismo set de campos que el panel: `address_components` habilita el llenado estructurado y
// `componentRestrictions` acota la búsqueda a México.
const AUTOCOMPLETE_OPTIONS: google.maps.places.AutocompleteOptions = {
  componentRestrictions: { country: 'mx' },
  fields: ['formatted_address', 'geometry', 'address_components'],
};

const DEFAULT_PLACEHOLDER = 'Escribe la dirección del comercio o mueve el marcador azul';

// Buscador estándar V4 (mismo lenguaje que los inputs del formulario): borde gris en reposo, sin
// sombra flotante — ya no se superpone al mapa, así que no necesita destacar sobre él.
const searchInputStyle: CSSProperties = {
  width: '100%',
  height: 40,
  padding: '0 12px',
  fontSize: 14,
  fontFamily: FONT,
  color: V4.primary,
  background: V4.white,
  border: `1.5px solid ${V4.grayLight}`,
  borderRadius: 8,
  outline: 'none',
  boxSizing: 'border-box',
};

export const CommerceAddressMap = ({
  isLoaded,
  center,
  hasSavedLocation,
  markerPosition,
  onPlaceSelected,
  onMarkerDragEnd,
  searchPlaceholder = DEFAULT_PLACEHOLDER,
  height = 300,
}: CommerceAddressMapProps) => {
  const acRef = useRef<google.maps.places.Autocomplete | null>(null);

  // Degradado mientras el script de Google Places aún no carga: conserva el alto/layout.
  if (!isLoaded) {
    return (
      <div
        style={{
          width: '100%',
          height,
          borderRadius: 8,
          background: `linear-gradient(135deg, ${V4.grayUltraLight} 0%, ${V4.grayLight} 100%)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: V4.grayUltraDark,
          fontSize: 13,
          fontFamily: FONT,
        }}
      >
        Cargando mapa…
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {/* Buscador ARRIBA del mapa — nunca superpuesto. */}
      <Autocomplete
        onLoad={(ac) => {
          acRef.current = ac;
        }}
        onPlaceChanged={() => {
          const place = acRef.current?.getPlace();
          if (place) onPlaceSelected(place);
        }}
        options={AUTOCOMPLETE_OPTIONS}
      >
        <input style={searchInputStyle} placeholder={searchPlaceholder} />
      </Autocomplete>

      <GoogleMap
        data-testid="google-map"
        options={MAP_OPTIONS}
        mapContainerStyle={{ position: 'relative', width: '100%', height, borderRadius: 8 }}
        center={center}
        zoom={hasSavedLocation ? 19 : 15}
      >
        <MarkerF
          position={markerPosition}
          draggable
          onDragEnd={({ latLng }) => {
            if (latLng) onMarkerDragEnd(latLng.lat(), latLng.lng());
          }}
          icon={{
            url: CustomMarker,
            scaledSize: new window.google.maps.Size(40, 65),
          }}
        />
      </GoogleMap>
    </div>
  );
};

export default CommerceAddressMap;
